import { identity } from "deso-protocol";

import { getFocusAuthHeaders } from "@/utils/api-utils";

import type { FocusNotification } from ".";
import { focusAPIInstance } from ".";
import type {
  NotificationCategory,
  NotificationOrderBy,
  NotificationStatus,
  NotificationSubCategory,
} from "./notifications-constants";

export type GetNotificationParams = {
  /**
   * @deprecated Use categories instead.
   */
  category?: NotificationCategory;

  /**
   * @deprecated Use subcategories instead.
   */
  subcategory?: NotificationSubCategory;
  categories?: NotificationCategory[];
  subcategories?: NotificationSubCategory[];
  excludedSubcategories?: NotificationSubCategory[];
  excludedCategories?: NotificationCategory[];
  status?: NotificationStatus;
  user?: string;
  post?: string;
  amountUsdCentsGte?: number;
  amountUsdCentsLte?: number;
  wealthUsdCentsGte?: number;
  wealthUsdCentsLte?: number;
  afterDaysAgo?: number;
  orderBy?: NotificationOrderBy;
  limit?: number;
  offset?: number;
  publicKey?: string;
};

export type NotificationRequestResponse = {
  Count: number;
  NextOffset: number;
  Notifications: FocusNotification[];
};

type EarningsRequestResponse = {
  EarningsUsdCents: {
    [k: string]: number;
  };
};

export const getNotificationsRequest = async (
  params: GetNotificationParams,
) => {
  const { publicKey: paramsPublicKey, ...restParams } = params;
  let publicKey = paramsPublicKey;

  if (!publicKey) {
    try {
      const { currentUser } = await identity.snapshot();
      publicKey = currentUser?.publicKey || "";
    } catch {
      console.log("Failed to get JWT or current user");
    }
  }

  if (!publicKey) {
    return {
      Count: 0,
      NextOffset: 0,
      Notifications: [],
    };
  }

  const { data } = await focusAPIInstance.get<NotificationRequestResponse>(
    `notifications/for/${publicKey}`,
    {
      headers: publicKey ? getFocusAuthHeaders("", publicKey).headers : {},
      params: restParams,
    },
  );
  return data;
};

export const getEarningsRequest = async (
  params: {
    afterDaysAgo?: number;
    publicKey?: string;
  } = {},
) => {
  const { publicKey: paramsPublicKey, ...restParams } = params;
  let publicKey = paramsPublicKey;

  if (!publicKey) {
    try {
      const { currentUser } = await identity.snapshot();
      publicKey = currentUser?.publicKey || "";
    } catch {
      console.log("Failed to get JWT or current user");
    }
  }

  if (!publicKey) {
    return {
      EarningsUsdCents: {},
    };
  }

  const { data } = await focusAPIInstance.get<EarningsRequestResponse>(
    `notifications/earnings/${publicKey}`,
    {
      headers: publicKey ? getFocusAuthHeaders("", publicKey).headers : {},
      params: restParams,
    },
  );
  return data;
};

export const markNotificationAsRead = async (
  publicKey: string,
  notificationId: string,
) => {
  const jwt = await identity.jwt();

  const { data } = await focusAPIInstance.post(
    `notifications/read/${notificationId}`,
    {},
    getFocusAuthHeaders(jwt, publicKey),
  );
  return data;
};

export const markAllNotificationsAsRead = async (publicKey: string) => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.post(
    `notifications/read`,
    {},
    getFocusAuthHeaders(jwt, publicKey),
  );
  return data;
};
